import React, { useCallback, useMemo, useState } from "react";
import { Download, FileText, GraduationCap, Pencil, ShoppingBag, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { pickLocalized } from "../../../../i18n/pickLocalized";
import useAdminLoad from "../../../../hooks/useAdminLoad";
import * as crmApi from "../../api/crmApi";
import { crmErrorMessage } from "../../api/crmErrors";
import { FEED_PAGE_SIZE } from "../../contract";
import { fmtDate } from "../../format";
import useClientTab from "../../hooks/useClientTab";
import CrmTextarea from "../../ui/CrmTextarea";
import CrmButton from "../../ui/CrmButton";
import CrmInput from "../../ui/CrmInput";
import CrmDialog from "../../ui/CrmDialog";
import CrmAlertDialog from "../../ui/CrmAlertDialog";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import SectionHeading from "../../ui/SectionHeading";
import { FOCUS_RING } from "../../ui/classes";
import { CRM } from "../../../../constants/testIds/crm";

const NOTE_MAX = 5000;
const ICON_BTN = `rounded-full p-1.5 text-eh-muted hover:bg-eh-cream-deep hover:text-eh-ink ${FOCUS_RING}`;

/** General free-text notes (non-clinical): newest first, author and date, edit in place. */
function GeneralNotes({ clientId }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const list = useClientTab(clientId, crmApi.listNotes, {}, FEED_PAGE_SIZE);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, done) => {
    setBusy(true);
    try { await fn(); done?.(); list.reload(); } catch (e) { toast.error(crmErrorMessage(e, t, tc)); } finally { setBusy(false); }
  };

  return (
    <section data-testid={CRM.clientGeneralNotes} aria-labelledby="crm-notes-general" className="space-y-3">
      <SectionHeading id="crm-notes-general" level={3} title={t("notes.generalTitle")} className="mb-0" />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <CrmTextarea rows={2} value={draft} maxLength={NOTE_MAX} onChange={(e) => setDraft(e.target.value)} placeholder={t("notes.placeholder")}
          aria-label={t("notes.generalTitle")} data-testid={CRM.clientGeneralNoteInput} />
        <CrmButton disabled={busy || !draft.trim()} data-testid={CRM.clientGeneralNoteAdd}
          onClick={() => run(() => crmApi.createNote(clientId, { body: draft.trim() }), () => { setDraft(""); toast.success(t("notes.added")); })}>
          {t("notes.add")}
        </CrmButton>
      </div>
      {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} />}
      {list.loaded && !list.loadError && list.rows.length === 0 && <p className="text-sm text-eh-muted">{t("notes.empty")}</p>}
      <ul className="space-y-2">
        {list.rows.map((n) => (
          <li key={n.id} data-testid={CRM.clientGeneralNote(n.id)} className="rounded-2xl bg-eh-cream px-4 py-3 text-sm text-eh-ink">
            {editing?.id === n.id ? (
              <div className="space-y-2">
                <CrmTextarea rows={3} value={editing.body} maxLength={NOTE_MAX} aria-label={t("notes.edit")} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                <div className="flex justify-end gap-3">
                  <CrmButton variant="link" size="sm" onClick={() => setEditing(null)}>{t("common.cancel")}</CrmButton>
                  <CrmButton size="sm" disabled={busy || !editing.body.trim()}
                    onClick={() => run(() => crmApi.updateNote(clientId, n.id, { body: editing.body.trim() }), () => setEditing(null))}>{t("common.save")}</CrmButton>
                </div>
              </div>
            ) : (
              <>
                <p className="whitespace-pre-wrap">{n.body}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-eh-muted">
                  <span>
                    {t("notes.byline", { who: n.author_email || "", date: fmtDate(n.created_at, lang) })}
                    {n.updated_at ? ` · ${t("notes.edited")}` : ""}
                  </span>
                  <span className="flex gap-1">
                    <button type="button" className={ICON_BTN} aria-label={t("notes.edit")} onClick={() => setEditing({ id: n.id, body: n.body })}><Pencil size={14} aria-hidden="true" /></button>
                    <button type="button" className={ICON_BTN} aria-label={t("notes.delete")} onClick={() => setRemoving(n)}><Trash2 size={14} aria-hidden="true" /></button>
                  </span>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      {list.rows.length < list.total && (
        <CrmButton variant="outline" size="sm" onClick={() => list.setPage(list.page + 1)}>{t("common.loadMore")}</CrmButton>
      )}
      <CrmAlertDialog open={!!removing} onOpenChange={(o) => { if (!o) setRemoving(null); }} title={t("notes.deleteTitle")}
        confirmLabel={t("notes.delete")} tone="danger" busy={busy}
        onConfirm={() => run(() => crmApi.deleteNote(clientId, removing.id), () => setRemoving(null))} />
    </section>
  );
}

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Documents attached to the client (uploaded to the media library, then linked). */
function Documents({ clientId }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const list = useClientTab(clientId, crmApi.listDocuments);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await crmApi.uploadDocument(clientId, file); toast.success(t("docs.attached")); list.reload(); }
    catch (e) { toast.error(crmErrorMessage(e, t, tc, "errors.uploadFailed")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await crmApi.deleteDocument(clientId, removing.id); setRemoving(null); list.reload(); }
    catch (e) { toast.error(crmErrorMessage(e, t, tc)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid={CRM.clientDocuments} aria-labelledby="crm-notes-docs" className="space-y-3">
      <SectionHeading id="crm-notes-docs" level={3} title={t("docs.title")} className="mb-0"
        right={(
          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-eh-border-soft px-3 py-1.5 text-xs text-eh-ink hover:bg-eh-bg focus-within:ring-2 focus-within:ring-eh-ink ${busy ? "opacity-50" : ""}`}>
            <Upload size={14} aria-hidden="true" /> {t("docs.upload")}
            <input type="file" className="sr-only" disabled={busy} accept="image/*,video/*,.pdf,.docx,.xlsx,.pptx,.txt,text/plain" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={CRM.clientDocumentUpload} />
          </label>
        )} />
      {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} />}
      {list.loaded && !list.loadError && list.rows.length === 0 && <p className="text-sm text-eh-muted">{t("docs.empty")}</p>}
      <ul className="space-y-2">
        {list.rows.map((d) => (
          <li key={d.id} data-testid={CRM.clientDocument(d.id)} className="flex items-center gap-3 rounded-2xl bg-eh-cream px-4 py-3 text-sm text-eh-ink">
            <FileText size={18} aria-hidden="true" className="shrink-0 text-eh-muted" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{d.title || d.filename}</span>
              <span className="text-xs text-eh-muted">{[d.mime, fmtSize(d.size), fmtDate(d.created_at, lang)].filter(Boolean).join(" · ")}</span>
            </span>
            {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className={ICON_BTN} aria-label={t("docs.download")}><Download size={16} aria-hidden="true" /></a>}
            <button type="button" className={ICON_BTN} aria-label={t("docs.delete")} onClick={() => setRemoving(d)}><Trash2 size={16} aria-hidden="true" /></button>
          </li>
        ))}
      </ul>
      <CrmAlertDialog open={!!removing} onOpenChange={(o) => { if (!o) setRemoving(null); }} title={t("docs.deleteTitle")}
        confirmLabel={t("docs.delete")} tone="danger" busy={busy} onConfirm={remove} />
    </section>
  );
}

const LINK_KINDS = Object.freeze({
  product: { label: "links.product", icon: ShoppingBag, load: crmApi.listProducts },
  course: { label: "links.course", icon: GraduationCap, load: crmApi.listCourses },
});

/** Offerings to attach: products or courses, by their localized name, minus those already attached. */
function LinkPicker({ kind, attached, onPick, onClose }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    const data = await LINK_KINDS[kind].load();
    setItems(Array.isArray(data) ? data : (data?.rows || data?.items || []));
  }, [kind]);
  const { loading, loadError, reload } = useAdminLoad(load);
  const label = (i) => pickLocalized(i, "name", lang) || pickLocalized(i, "title", lang) || i.name || i.title || i.id;
  const shown = useMemo(() => {
    const taken = new Set(attached.map((a) => a.ref_id));
    const needle = q.trim().toLowerCase();
    return items.filter((i) => !taken.has(i.id) && (!needle || label(i).toLowerCase().includes(needle)));
  }, [items, attached, q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} title={t("links.pickTitle", { kind: t(LINK_KINDS[kind].label) })} testId={CRM.clientLinkPicker}>
      <CrmInput type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("links.search")} aria-label={t("links.search")} />
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} className="mt-3" />}
      {!loading && !loadError && shown.length === 0 && <p className="mt-3 text-sm text-eh-muted">{t("links.none")}</p>}
      <ul className="mt-3 max-h-[50vh] space-y-1 overflow-y-auto" data-lenis-prevent>
        {shown.map((i) => (
          <li key={i.id}>
            <button type="button" onClick={() => onPick(i, label(i))} className={`w-full rounded-2xl px-3 py-2 text-left text-sm hover:bg-eh-cream ${FOCUS_RING}`}>{label(i)}</button>
          </li>
        ))}
      </ul>
    </CrmDialog>
  );
}

/** Offerings attached to the client by hand (products, courses). */
function Links({ clientId }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const list = useClientTab(clientId, crmApi.listLinks);
  const [picking, setPicking] = useState(null);
  const run = async (fn) => {
    try { await fn(); list.reload(); } catch (e) { toast.error(crmErrorMessage(e, t, tc)); }
  };
  return (
    <section data-testid={CRM.clientLinks} aria-labelledby="crm-notes-links" className="space-y-3">
      <SectionHeading id="crm-notes-links" level={3} title={t("links.title")} className="mb-0"
        right={(
          <span className="flex gap-2">
            {Object.entries(LINK_KINDS).map(([k, m]) => (
              <CrmButton key={k} variant="outline" size="sm" onClick={() => setPicking(k)} data-testid={CRM.clientLinkAdd(k)}>
                <m.icon size={14} aria-hidden="true" /> {t("links.add", { kind: t(m.label) })}
              </CrmButton>
            ))}
          </span>
        )} />
      {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} />}
      {list.loaded && !list.loadError && list.rows.length === 0 && <p className="text-sm text-eh-muted">{t("links.empty")}</p>}
      <ul className="space-y-2">
        {list.rows.map((a) => {
          const Icon = LINK_KINDS[a.kind]?.icon || FileText;
          return (
            <li key={a.id} data-testid={CRM.clientLink(a.id)} className="flex items-center gap-3 rounded-2xl bg-eh-cream px-4 py-3 text-sm text-eh-ink">
              <Icon size={16} aria-hidden="true" className="shrink-0 text-eh-muted" />
              <span className="min-w-0 flex-1 truncate font-medium">{a.label || a.ref_id}</span>
              <span className="text-xs text-eh-muted">{fmtDate(a.created_at, lang)}</span>
              <button type="button" className={ICON_BTN} aria-label={t("links.remove")} onClick={() => run(() => crmApi.deleteLink(clientId, a.id))}><Trash2 size={16} aria-hidden="true" /></button>
            </li>
          );
        })}
      </ul>
      {picking && (
        <LinkPicker kind={picking} attached={list.rows.filter((a) => a.kind === picking)} onClose={() => setPicking(null)}
          onPick={(item, label) => run(() => crmApi.createLink(clientId, { kind: picking, ref_id: item.id, label, notes: "" })).then(() => setPicking(null))} />
      )}
    </section>
  );
}

/**
 * Notes: general notes (spec: "General free-text notes (non-clinical), newest first,
 * author and date"), then the client's documents and attached offerings — carried over
 * from the retired Clients page, now keyed by client id (contract v1.2 CR-T-50).
 */
export default function NotesTab({ clientId }) {
  return (
    <div className="space-y-8">
      <GeneralNotes clientId={clientId} />
      <Documents clientId={clientId} />
      <Links clientId={clientId} />
    </div>
  );
}
